import {useCallback, useEffect, useMemo, useRef} from 'preact/hooks';

import {
  BrandHandle,
  DeliveryEstimateContextProvider,
  forceDisplayDates,
  QueryMetadata,
  useFetchDeliveryEstimate,
} from 'utilities/delivery-promise';
import {useLocation} from 'utilities/location';
import {usePromisePresentment} from 'utilities/promise-presenter';
import {useTheme} from 'utilities/color';
import {ErrorMessage, ShopPromiseBrand} from 'brands/ShopPromiseBrand';
import {AmazonLayout, ShopPromiseLayout} from 'layouts';
import {useScopedErrorBoundary} from 'utilities/error-logger';
import {ProductDetailsContextProvider} from 'utilities/product';
import {getConfiguration} from 'utilities/configuration';
import BuyWithPrimeBrand from 'brands/BuyWithPrimeBrand/BuyWithPrimeBrand';
import {PromiseShell} from 'components/PromiseShell/PromiseShell';
import {
  trackDeliveryPromiseResponse,
  trackDeliveryPromiseVisible,
} from 'utilities/tracking';
import {usePDPPerformanceMetrics} from 'utilities/telemetry/hooks/pdp-performance-metrics.hook';
import {useEnterViewportObserver} from 'utilities/enter-viewport-observer/useEnterViewportObserver';
import {useTrackingRenderProcessing} from 'utilities/tracking/hooks';

import {useSDKLoader} from './hooks';

interface Props {
  variantId?: number;
  sellingPlanId?: number;
}

function InternalPDP({
  variantId: defaultVariantId,
  sellingPlanId: defaultSellingPlan,
}: Props) {
  const {postalCode} = useLocation();
  const {theme, fontOverride} = useTheme();
  // If the there is a postalCode override, the module has been seen at some point in the
  // buyer's journey.
  const moduleHasBeenSeen = useRef(Boolean(postalCode));
  const {releaseStage} = getConfiguration();

  const {
    deliveryEstimate,
    isLoading: isRequestLoading,
    hasError,
  } = useFetchDeliveryEstimate(
    defaultVariantId,
    defaultSellingPlan,
    postalCode ?? undefined,
  );
  const requestId = (deliveryEstimate as QueryMetadata)?.requestId || '';

  const promiseIntersectionRef = useEnterViewportObserver(
    useCallback(() => {
      if (!isRequestLoading) {
        trackDeliveryPromiseVisible(requestId);
      }
    }, [isRequestLoading, requestId]),
  );

  const presentment = usePromisePresentment(deliveryEstimate);

  const {status: SDKStatus, isLoading: isSDKLoading} = useSDKLoader(
    presentment,
    deliveryEstimate,
  );

  const isModuleLoading =
    (presentment.sdkURL && isSDKLoading) || isRequestLoading;

  usePDPPerformanceMetrics(!isModuleLoading);

  const BrandLayout = useMemo(() => {
    if (presentment.brandHandle === BrandHandle.Prime) {
      // todo: lazy loading of the layout is complicated due to styles
      return AmazonLayout;
    }

    return ShopPromiseLayout;
  }, [presentment.brandHandle]);

  const Brand = useMemo(() => {
    if (presentment.brandHandle === BrandHandle.Prime) {
      return (
        <BuyWithPrimeBrand promisePresentment={presentment} theme={theme} />
      );
    }

    return (
      <ShopPromiseBrand
        promisePresentment={presentment}
        hasPostalCodeOverride={Boolean(postalCode)}
        isLoading={isRequestLoading}
      />
    );
  }, [presentment, postalCode, isRequestLoading, theme]);

  useEffect(() => {
    if (
      !isModuleLoading &&
      deliveryEstimate &&
      !presentment.hidePromise &&
      presentment.brandHandle !== BrandHandle.Prime
    ) {
      moduleHasBeenSeen.current = true;
    }
  }, [
    deliveryEstimate,
    isModuleLoading,
    presentment.hidePromise,
    presentment.brandHandle,
  ]);

  const showMarkup = useMemo(() => {
    const hideBwpPromise =
      presentment.brandHandle === BrandHandle.Prime
        ? SDKStatus === 'error'
        : false;

    return (
      Boolean(deliveryEstimate) && !presentment.hidePromise && !hideBwpPromise
    );
  }, [
    SDKStatus,
    deliveryEstimate,
    presentment.brandHandle,
    presentment.hidePromise,
  ]);

  const showError = useMemo(() => {
    const noPromise = deliveryEstimate && presentment.hidePromise;
    return (
      (moduleHasBeenSeen.current && hasError) ||
      (moduleHasBeenSeen.current && noPromise)
    );
  }, [deliveryEstimate, presentment.hidePromise, hasError]);

  useTrackingRenderProcessing({
    isModuleLoading,
    hasError,
    showError: showError || false,
    requestId,
    showMarkup,
    presentment,
    forceDisplayDates,
    deliveryEstimate,
    postalCode,
  });

  useEffect(() => {
    if (!showError && showMarkup && !isModuleLoading) {
      trackDeliveryPromiseResponse(deliveryEstimate, postalCode);
    }
  }, [showError, showMarkup, isModuleLoading, deliveryEstimate, postalCode]);

  if (showError) {
    return (
      <ErrorMessage
        fontOverride={fontOverride}
        isLoading={isModuleLoading}
        theme={theme}
        errorCode={errorCode(hasError, presentment.hidePromise)}
      />
    );
  }

  if (releaseStage === 'development') {
    // eslint-disable-next-line no-console
    console.log(
      `[ShopPromise] Presentment [showMarkup=${showMarkup}] [isModuleLoading=${isModuleLoading}]`,
      presentment,
    );
  }

  const promiseMarkup = (
    <PromiseShell
      isLoading={isModuleLoading}
      promiseIntersectionRef={promiseIntersectionRef}
    >
      <BrandLayout
        isHidden={false}
        isLoading={isModuleLoading}
        theme={theme}
        fontOverride={fontOverride}
        hasBrand={presentment.showLogo}
      >
        {Brand}
      </BrandLayout>
    </PromiseShell>
  );
  return <>{showMarkup && promiseMarkup}</>;
}

export function ProductPage({variantId, sellingPlanId}: Props) {
  const error = useScopedErrorBoundary('ProductPage');

  if (error) {
    return <ErrorMessage errorCode="runtime_error" />;
  }

  return (
    <ProductDetailsContextProvider>
      <DeliveryEstimateContextProvider>
        <InternalPDP variantId={variantId} sellingPlanId={sellingPlanId} />
      </DeliveryEstimateContextProvider>
    </ProductDetailsContextProvider>
  );
}

function errorCode(apiFetchError: boolean, hidePromise?: boolean) {
  switch (true) {
    case apiFetchError:
      return 'api_fetch_error';
    case hidePromise:
      return 'promise_hidden';
    default:
      return undefined;
  }
}
